import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/toast';
import { AuthTabs } from '@/components/AuthTabs';
import { authErrorMessage } from '@/lib/authErrors';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signInEmail, signInGoogle } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  async function handle(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await signInEmail(email, password);
      toast({ title: 'Signed in', description: `Welcome back, ${email.trim()}.`, variant: 'success' });
      navigate('/');
    } catch (err) {
      toast({ title: 'Sign in failed', description: authErrorMessage(err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <AuthTabs active="login" />
          <div>
            <CardTitle>Sign in</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Welcome back. Sign in to your account.
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handle} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="email">Email or username</Label>
              <Input
                id="email"
                type="text"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <div className="relative flex items-center justify-center text-xs text-muted-foreground">
            <span className="relative bg-card px-2">or</span>
            <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={async () => {
              try {
                await signInGoogle();
                toast({ title: 'Signed in', variant: 'success' });
                navigate('/');
              } catch (err) {
                toast({ title: 'Google sign in failed', description: authErrorMessage(err), variant: 'destructive' });
              }
            }}
          >
            Continue with Google
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary font-medium">
              Sign up
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
